import { SystemError } from './validateImage';

const TARGET_SIZE = 1024;

/**
 * Takes a cropped canvas from Cropper.js and produces a JPEG File capped at
 * 1024px on the longest side.
 *
 * @param canvas - The cropped canvas from Cropper.js
 * @returns Promise resolving to a JPEG File (max 1024px)
 * @throws {SystemError} If browser fails to encode JPEG
 */
export const resizeImage = async (canvas: HTMLCanvasElement): Promise<File> => {
  const { width, height } = canvas;
  const maxDimension = Math.max(width, height);
  const shouldDownscale = maxDimension > TARGET_SIZE;

  const bitmap = await createImageBitmap(canvas, {
    ...(shouldDownscale
      ? {
          resizeWidth: Math.round((width / maxDimension) * TARGET_SIZE),
          resizeHeight: Math.round((height / maxDimension) * TARGET_SIZE),
        }
      : {}),
    resizeQuality: 'high',
  });

  return bitmapToJPEG(bitmap);
};

async function bitmapToJPEG(bitmap: ImageBitmap): Promise<File> {
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    bitmap.close();
    throw new SystemError('Failed to get canvas context');
  }

  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) {
          if (result.type !== 'image/jpeg') {
            reject(
              new SystemError(
                `Browser failed to encode JPEG. Got ${result.type} instead.`,
              ),
            );
          } else {
            resolve(result);
          }
        } else {
          reject(new SystemError('Failed to create blob from canvas'));
        }
      },
      'image/jpeg',
      0.8,
    );
  });

  return new File([blob], 'avatar.jpg', {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}
